import {sanitizeUrl} from '@braintree/sanitize-url';
import {
  Selectors,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library';
import {useSearchParams} from 'react-router-dom';
import {isAbsoluteUrl} from 'utils/utils';

const authenticatedReturn = '/rewards/profile';
const unauthenticatedReturn = '/rewards/signin';

export const useGetForwardUrl = () => {
  const [searchParams] = useSearchParams();
  const isLoggedIn = useWidgetState(Selectors.isLoggedInSelector);
  const fallbackForwardUrl = isLoggedIn
    ? authenticatedReturn
    : unauthenticatedReturn;

  const forwardUrl = searchParams.get('forwardUrl') ?? fallbackForwardUrl;

  const sanitizedForwardUrl = (() => {
    let decoded: string = forwardUrl;
    for (let i = 0; i < 5; i++) {
      try {
        const next = decodeURIComponent(decoded);
        if (next === decoded) break;
        decoded = next;
      } catch {
        break;
      }
    }

    // Canonicalize before any checks. Browsers discard embedded control
    // characters and treat a backslash as a slash, so both have to be resolved
    // up front or a check can pass on a string the browser never sees. Every
    // check below, and the value returned, uses this one canonical string.
    const canonical = decoded
      // eslint-disable-next-line no-control-regex -- matching control characters is the intent here
      .replace(/[\x00-\x1f\x7f]/g, '')
      .replace(/\\/g, '/');

    // Reject dangerous protocols and off-site redirects
    if (/^[a-z][a-z0-9+.-]*:/i.test(canonical)) return null; // any scheme, including javascript:
    if (canonical.startsWith('//')) return null; // protocol-relative off-site
    if (!canonical.startsWith('/')) return null; // allowlist: same-origin paths only

    // sanitizeUrl can rewrite its input, so re-assert the allowlist against the
    // exact string handed back rather than trusting the pre-sanitized one.
    const sanitized = sanitizeUrl(canonical);
    if (!sanitized.startsWith('/') || sanitized.startsWith('//')) return null;

    return sanitized;
  })();

  // prevent off-site links
  if (isAbsoluteUrl(sanitizedForwardUrl)) {
    return fallbackForwardUrl;
  }

  // A rejected url falls back to the safe default rather than no redirect at all
  if (!sanitizedForwardUrl || sanitizedForwardUrl === 'about:blank') {
    return fallbackForwardUrl;
  }

  return sanitizedForwardUrl;
};
